<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDeviceStore } from '@/stores/device'
import { useEqStore } from '@/stores/eq'
import { useChijiStore } from '@/stores/chiji'
import { useRoute } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'

const { t } = useI18n()
const deviceStore = useDeviceStore()
const eqStore = useEqStore()
const chijiStore = useChijiStore()
const route = useRoute()

// 菜单状态
const showEqDropdown = ref(false)
const showRgbDropdown = ref(false)
const showVolumePopup = ref(false)
const showMicPopup = ref(false)

const noisePopupEl = ref<HTMLElement | null>(null)
const noiseTrackEl = ref<HTMLElement | null>(null)
const noiseThumbEl = ref<HTMLElement | null>(null)
const noiseProgressEl = ref<HTMLElement | null>(null)
const noiseValueEl = ref<HTMLElement | null>(null)

const noiseDragging = ref(false)
let lastNoiseLevelSent: number | null = null

const activeFooterPopup = ref<'eq' | 'rgb' | 'noise' | 'volume' | 'mic' | 'audioMix' | null>(null)

let hideTimer: number | null = null

const clearHideTimer = () => {
  if (hideTimer != null) {
    window.clearTimeout(hideTimer)
    hideTimer = null
  }
}

const closeAllPopups = () => {
  clearHideTimer()
  showEqDropdown.value = false
  showRgbDropdown.value = false
  showVolumePopup.value = false
  showMicPopup.value = false
  activeFooterPopup.value = null
}

const openPopup = (popup: 'eq' | 'rgb' | 'noise' | 'volume' | 'mic' | 'audioMix') => {
  clearHideTimer()
  activeFooterPopup.value = popup

  showEqDropdown.value = popup === 'eq'
  showRgbDropdown.value = popup === 'rgb'
  showVolumePopup.value = popup === 'volume'
  showMicPopup.value = popup === 'mic'
}

const scheduleClosePopup = (popup: 'eq' | 'rgb' | 'noise' | 'volume' | 'mic' | 'audioMix') => {
  clearHideTimer()
  hideTimer = window.setTimeout(() => {
    if (activeFooterPopup.value === popup) {
      closeAllPopups()
    }
  }, 300)
}

const audioDragging = ref(false)

const clampAudioValue = (v: number) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return 0
  return Math.max(0, Math.min(100, n))
}

const getAudioBalanceSendValue = (positionValue: number) => {
  const pos = clampAudioValue(positionValue)
  return Math.max(0, Math.min(200, Math.round(pos * 2)))
}

let lastAudioBalanceSentValue: number | null = null

// 旧版显示值：中心(50)=>100/100；往左衰减左侧；往右衰减右侧
const getAudioBalanceDisplayValue = (positionValue: number) => {
  const pos = clampAudioValue(positionValue)
  const distanceFromCenter = Math.abs(pos - 50)
  return Math.max(0, Math.min(100, Math.round(100 - (distanceFromCenter / 50) * 100)))
}

const audioSliderEl = ref<HTMLElement | null>(null)
const audioModeMenuEl = ref<HTMLElement | null>(null)

const leftChannelLabel = computed(() => {
  return deviceStore.connectionType === '2.4G' ? '2.4G' : 'USB'
})

const audioBalanceValue = computed(() => clampAudioValue(deviceStore.audioBalanceValue))

const leftDisplayPercent = computed(() => {
  const pos = audioBalanceValue.value
  const displayValue = getAudioBalanceDisplayValue(pos)
  if (pos < 50) return displayValue
  return 100
})

const rightDisplayPercent = computed(() => {
  const pos = audioBalanceValue.value
  const displayValue = getAudioBalanceDisplayValue(pos)
  if (pos > 50) return displayValue
  return 100
})

const applyAudioSliderCssVars = (value?: number) => {
  const el = audioSliderEl.value
  if (!el) return
  const pos = clampAudioValue(value ?? audioBalanceValue.value)
  el.style.setProperty('--ab-pos', String(pos))
  if (pos >= 50) {
    el.style.setProperty('--ab-fill-start', '50')
    el.style.setProperty('--ab-fill-width', String(pos - 50))
  } else {
    el.style.setProperty('--ab-fill-start', String(pos))
    el.style.setProperty('--ab-fill-width', String(50 - pos))
  }
}

watch(
  () => deviceStore.audioBalanceValue,
  () => {
    applyAudioSliderCssVars()
  },
)

const setAudioBalanceByClientX = (clientX: number, shouldSend: boolean) => {
  const el = audioSliderEl.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  if (!rect.width) return
  const percent = ((clientX - rect.left) / rect.width) * 100
  const nextValue = clampAudioValue(percent)

  applyAudioSliderCssVars(nextValue)
  if (shouldSend) {
    const nextSendValue = getAudioBalanceSendValue(nextValue)
    if (lastAudioBalanceSentValue !== nextSendValue) {
      lastAudioBalanceSentValue = nextSendValue
      void deviceStore.setAudioBalance(nextValue)
    }
  }
}

const onAudioSliderClick = (e: MouseEvent) => {
  setAudioBalanceByClientX(e.clientX, true)
}

const onAudioPointerDown = (e: PointerEvent) => {
  const target = e.target as HTMLElement
  const isThumb = target?.classList?.contains('audio-balance-thumb')
  const inSlider = !!(target && (target.classList?.contains('audio-balance-slider') || target.closest?.('.audio-balance-slider')))
  if (!isThumb && !inSlider) return

  audioDragging.value = true
  ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
  setAudioBalanceByClientX(e.clientX, true)
}

const onAudioPointerMove = (e: PointerEvent) => {
  if (!audioDragging.value) return
  setAudioBalanceByClientX(e.clientX, true)
}

const stopAudioDragging = () => {
  audioDragging.value = false

  // 对齐旧版：拖动结束后，如果鼠标已离开容器，则关闭弹窗
  const container = document.querySelector('.audio-mode-container') as HTMLElement | null
  if (container && !container.matches(':hover') && activeFooterPopup.value === 'audioMix') {
    closeAllPopups()
  }
}

// EQ模式选项 - 对齐原项目
const eqModes = [
  { id: 'game', label: 'Game Mode' },
  { id: 'music', label: t('eq.music', '音乐') },
  { id: 'movie', label: t('eq.movie', '电影') },
]

// 计算当前EQ显示文本
const currentEqLabel = computed(() => {
  const mode = eqStore.activeMode
  if (!eqStore.currentModeState?.enabled) return 'OFF'
  const found = eqModes.find(m => m.id === mode)
  return found?.label || 'OFF'
})

// RGB模式选项 - 对齐原项目（耳机和麦克风不同）
const headphoneRgbModes = [
  { id: 'breathing', label: t('mainrgb.breathing', 'Purple Rhythm') },
  { id: 'gradient', label: t('mainrgb.gradient', 'Cyan Rhythm') },
  { id: 'chase', label: t('mainrgb.marquee', 'Blue Rhythm') },
]

const micRgbModes = [
  { id: 'spectrum', label: t('mainrgb.mic_gradient', 'Spectrum') },
  { id: 'breathing', label: t('mainrgb.mic_breathing', 'Breathing') },
  { id: 'red', label: t('mainrgb.red', 'Red') },
  { id: 'blue', label: t('mainrgb.blue', 'Blue') },
  { id: 'green', label: t('mainrgb.green', 'Green') },
  { id: 'purple', label: t('mainrgb.purple', 'Purple') },
  { id: 'yellow', label: t('mainrgb.yellow', 'Yellow') },
  { id: 'cyan', label: t('mainrgb.cyan', 'Cyan') },
  { id: 'white', label: t('mainrgb.white', 'White') },
]

const rgbModes = computed(() => {
  return deviceStore.isHeadPhone ? headphoneRgbModes : micRgbModes
})

// 对齐旧版 server-1.js: RGB mode byte mapping
// 00=OFF, 01=chase, 02=gradient/spectrum, 03=breathing, 04..0A=单色
const rgbIdToModeByte: Record<string, number> = {
  off: 0x00,
  chase: 0x01,
  gradient: 0x02,
  spectrum: 0x02,
  breathing: 0x03,
  red: 0x04,
  blue: 0x05,
  green: 0x06,
  purple: 0x07,
  yellow: 0x08,
  cyan: 0x09,
  white: 0x0A,
}

const rgbModeByteToId = (mode: number): string => {
  if (mode === 0x01) return 'chase'
  if (mode === 0x02) return deviceStore.isHeadPhone ? 'gradient' : 'spectrum'
  if (mode === 0x03) return 'breathing'
  if (mode === 0x04) return 'red'
  if (mode === 0x05) return 'blue'
  if (mode === 0x06) return 'green'
  if (mode === 0x07) return 'purple'
  if (mode === 0x08) return 'yellow'
  if (mode === 0x09) return 'cyan'
  if (mode === 0x0A) return 'white'
  return 'off'
}

const currentRgbLabel = computed(() => {
  if (!deviceStore.rgbEnabled) return 'OFF'
  const modeId = rgbModeByteToId(deviceStore.rgbMode)
  const mode = rgbModes.value.find(m => m.id === modeId)
  return mode?.label || 'OFF'
})

// 降噪档位显示
const noiseLevelText = computed(() => {
  const level = deviceStore.noiseLevel || 1
  return `${level}<span class="unit">${t('footer.level', '档')}</span>`
})

const noiseLevel = computed(() => deviceStore.noiseLevel || 1)

const applyNoiseSliderUI = (level: number) => {
  const clamped = Math.max(1, Math.min(6, Math.round(level)))
  const percent = ((clamped - 1) / 5) * 100

  if (noiseThumbEl.value) noiseThumbEl.value.style.left = `${percent}%`
  if (noiseProgressEl.value) noiseProgressEl.value.style.width = `${percent}%`
  if (noiseValueEl.value) noiseValueEl.value.textContent = String(clamped)
}

const setNoiseLevelByClientX = (clientX: number, shouldSend: boolean) => {
  const el = noiseTrackEl.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  if (!rect.width) return
  const ratio = (clientX - rect.left) / rect.width
  const nextLevel = Math.max(1, Math.min(6, Math.round(ratio * 5) + 1))

  applyNoiseSliderUI(nextLevel)

  if (deviceStore.noiseEnabled) {
    if (shouldSend) {
      if (lastNoiseLevelSent !== nextLevel) {
        lastNoiseLevelSent = nextLevel
        void deviceStore.setNoiseLevel(nextLevel)
      }
    }
  } else {
    void deviceStore.setNoiseLevel(nextLevel)
  }
}

const onNoisePointerDown = (e: PointerEvent) => {
  noiseDragging.value = true
  openPopup('noise')
  lastNoiseLevelSent = null
  ;(e.currentTarget as HTMLElement)?.setPointerCapture?.(e.pointerId)
  setNoiseLevelByClientX(e.clientX, true)
}

const onNoisePointerMove = (e: PointerEvent) => {
  if (!noiseDragging.value) return
  setNoiseLevelByClientX(e.clientX, true)
}

const stopNoiseDragging = () => {
  if (!noiseDragging.value) return
  noiseDragging.value = false

  const container = document.querySelector('.noise-container') as HTMLElement | null
  if (container && !container.matches(':hover') && activeFooterPopup.value === 'noise') {
    closeAllPopups()
  }
}

const onNoiseTrackClick = (e: MouseEvent) => {
  lastNoiseLevelSent = null
  setNoiseLevelByClientX(e.clientX, true)
}

const incNoiseLevel = () => {
  const next = Math.min(6, noiseLevel.value + 1)
  applyNoiseSliderUI(next)
  if (deviceStore.noiseEnabled) {
    void deviceStore.setNoiseLevel(next)
  } else {
    void deviceStore.setNoiseLevel(next)
  }
}

const decNoiseLevel = () => {
  const next = Math.max(1, noiseLevel.value - 1)
  applyNoiseSliderUI(next)
  if (deviceStore.noiseEnabled) {
    void deviceStore.setNoiseLevel(next)
  } else {
    void deviceStore.setNoiseLevel(next)
  }
}

const chooseNoiseLevel = (level: number) => {
  const next = Math.max(1, Math.min(6, Math.round(level)))
  applyNoiseSliderUI(next)
  lastNoiseLevelSent = null
  void deviceStore.setNoiseLevel(next)
}

// 音量值（与设备状态同步）
const volume = computed({
  get: () => deviceStore.speakerVolume,
  set: (v: number) => {
    void deviceStore.setSpeakerVolume(v)
  },
})

const micVolume = computed({
  get: () => deviceStore.micVolume,
  set: (v: number) => {
    void deviceStore.setMicVolume(v)
  },
})

onMounted(() => {
  void nextTick(() => {
    applyAudioSliderCssVars()
    applyNoiseSliderUI(deviceStore.noiseLevel || 1)
  })
  document.addEventListener('pointerup', stopAudioDragging)
  document.addEventListener('pointercancel', stopAudioDragging)
  document.addEventListener('pointerup', stopNoiseDragging)
  document.addEventListener('pointercancel', stopNoiseDragging)
})

onUnmounted(() => {
  clearHideTimer()
  document.removeEventListener('pointerup', stopAudioDragging)
  document.removeEventListener('pointercancel', stopAudioDragging)
  document.removeEventListener('pointerup', stopNoiseDragging)
  document.removeEventListener('pointercancel', stopNoiseDragging)
})

// 切换电源 - 对齐原项目
const togglePower = async () => {
  // 旧版：USB 模式下提示 Ineffective
  if (deviceStore.connectionType === 'USB') {
    ElMessage({
      message: 'Ineffective',
      duration: 1200,
    })
    return
  }

  // 旧版：只有 2.4G + 支持电源控制才可用
  if (!deviceStore.canUsePowerControl) return

  // 旧版：开机 -> toast 4.5s；关机 -> confirm
  if (deviceStore.isPowerOff) {
    ElMessage({
      message: route.path === '/' ? (t('footer.poweringOn', 'Powering on...') as any) : (t('footer.poweringOn', 'Powering on...') as any),
      duration: 4500,
    })
    await deviceStore.setPowerOn(true)
    return
  }

  try {
    await ElMessageBox.confirm(
      `
        <div style="text-align: left;">
          <div style="font-size: 16px;margin-bottom: 8px;">${t('footer.powerOffConfirmTitle', '确定要进行设备关机吗？')}</div>
          <div style="font-size: 14px; color:rgba(255, 255, 255, 0.75);">${t('footer.powerOffConfirmDesc', '确认关机后，将会进入弱连接状态，此时耳机会有一定功耗。')}</div>
        </div>
      `,
      '',
      {
        confirmButtonText: t('footer.powerOffConfirmBtn', '确认关机') as any,
        cancelButtonText: t('footer.cancel', '取消') as any,
        dangerouslyUseHTMLString: true,
        showClose: false,
        closeOnClickModal: false,
        closeOnPressEscape: true,
      },
    )
  } catch {
    return
  }

  await deviceStore.setPowerOn(false)
}

// 旧版：点击 EQ 主按钮做开/关切换（不改变当前模式）
const toggleEqEnabled = async () => {
  await eqStore.setEqEnabled(!eqStore.isEnabled)
}

// 旧版：点击 RGB 主按钮做开/关切换（不改变当前模式）
const toggleRgbEnabled = async () => {
  await deviceStore.setRgbEnabled(!deviceStore.rgbEnabled)
}

// 切换EQ模式
const switchEqMode = (modeId: string) => {
  if (modeId === 'off') {
    eqStore.setEqEnabled(false)
  } else {
    eqStore.setActiveMode(modeId as any)
    eqStore.setEqEnabled(true)
  }
  showEqDropdown.value = false
  activeFooterPopup.value = null
}

// 切换RGB模式
const switchRgbMode = (modeId: string) => {
  const modeByte = rgbIdToModeByte[modeId] ?? 0x00
  void deviceStore.setRgbMode(modeByte)
  void deviceStore.setRgbEnabled(modeId !== 'off')
  showRgbDropdown.value = false
  activeFooterPopup.value = null
}

// 对齐旧版：点击图标直接开/关 AI 降噪（不弹下拉）
const toggleNoiseEnabled = async () => {
  await deviceStore.setNoiseEnabled(!deviceStore.noiseEnabled)
  void nextTick(() => {
    lastNoiseLevelSent = null
    applyNoiseSliderUI(deviceStore.noiseLevel || 1)
  })
}

// 打开一键吃鸡
const openChiji = async () => {
  await chijiStore.setEnabled(!chijiStore.enabled)
}

watch(
  () => route.path,
  () => {
    closeAllPopups()
  },
)

// 对齐旧版: 点击图标切换静音
const toggleSpeakerMute = async () => {
  await deviceStore.toggleSpeakerMute()
}

const toggleMicMute = async () => {
  await deviceStore.toggleMicMute()
}

const speakerIconSrc = computed(() => {
  return deviceStore.speakerMuted ? '/img/footer/footer_voice_off.png' : '/img/footer/footer_voice.png'
})

const micIconSrc = computed(() => {
  return deviceStore.micMuted ? '/img/footer/footer_mic_off.png' : '/img/footer/footer_mic.png'
})

const isEqEnabled = computed(() => !!eqStore.currentModeState?.enabled)
const isRgbEnabled = computed(() => !!deviceStore.rgbEnabled)
const isNoiseEnabled = computed(() => !!deviceStore.noiseEnabled)

const toggleAudioMixSplit = async () => {
  await deviceStore.setAudioSplit(!deviceStore.audioSplitEnabled)
}
</script>

<template>
  <footer v-if="deviceStore.isConnected && deviceStore.isDeviceConnected && route.path === '/'" class="bottom-menu">
    <div class="footer-menu">
      <!-- 电源按钮 -->
      <div v-if="deviceStore.canUsePowerControl" class="footer-menu-container">
        <div class="icon-box icon-box-on" @click="togglePower">
          <a href="javascript:;" class="footer-power">
            <img alt="电源" class="footer-icon" src="/img/footer/on_icon.png">
          </a>
        </div>
      </div>

      <!-- EQ切换 -->
      <div v-if="deviceStore.canUseEq" class="footer-menu-container eq-container">
        <div
          class="icon-box"
          :class="{ selected: isEqEnabled }"
          @click.stop="toggleEqEnabled"
          @mouseenter="openPopup('eq')"
          @mouseleave="scheduleClosePopup('eq')"
        >
          <a href="javascript:;">
            <img alt="EQ" class="footer-icon" :class="{ 'footer-EQ-on': isEqEnabled }" src="/img/footer/footer_EQ_icon.png">
          </a>
          <span class="footer-text">{{ currentEqLabel }}</span>
        </div>
        <!-- EQ下拉菜单 -->
        <div
          v-if="showEqDropdown"
          class="popup-dropdown"
          @mouseenter="openPopup('eq')"
          @mouseleave="scheduleClosePopup('eq')"
        >
          <div
            v-for="mode in eqModes"
            :key="mode.id"
            class="dropdown-item"
            :class="{ active: currentEqLabel === mode.label }"
            @click.stop="switchEqMode(mode.id)"
          >
            {{ mode.label }}
          </div>
        </div>
      </div>

      <!-- RGB控制 -->
      <div v-if="deviceStore.canUseLed" class="footer-menu-container rgb-container">
        <div
          class="icon-box"
          :class="{ selected: isRgbEnabled }"
          @click.stop="toggleRgbEnabled"
          @mouseenter="openPopup('rgb')"
          @mouseleave="scheduleClosePopup('rgb')"
        >
          <a href="javascript:;">
            <img alt="RGB" class="footer-icon" :class="{ 'footer-RGB-on': isRgbEnabled }" src="/img/footer/footer_rgb_icon.png">
          </a>
          <span class="footer-text">{{ currentRgbLabel }}</span>
        </div>
        <!-- RGB下拉菜单 -->
        <div
          v-if="showRgbDropdown"
          class="popup-dropdown"
          @mouseenter="openPopup('rgb')"
          @mouseleave="scheduleClosePopup('rgb')"
        >
          <div
            v-for="mode in rgbModes"
            :key="mode.id"
            class="dropdown-item"
            :class="{ active: currentRgbLabel === mode.label }"
            @click.stop="switchRgbMode(mode.id)"
          >
            {{ mode.label }}
          </div>
        </div>
      </div>

      <!-- AI降噪档位 -->
      <div v-if="deviceStore.canUseNoiseReduction" class="footer-menu-container noise-container">
        <div
          class="icon-box"
          :class="{ selected: isNoiseEnabled, 'is-popup-open': activeFooterPopup === 'noise' || noiseDragging }"         
          @click.stop="toggleNoiseEnabled"
        >
          <a href="javascript:;">
            <img alt="AI降噪" class="footer-icon" :class="{ 'footer-ENC-on': isNoiseEnabled }" src="/img/footer/footer_ENC_icon.png">
          </a>
          <span class="footer-text noise-level" v-html="noiseLevelText"></span>
        </div>

       
      </div>

      <!-- 音量控制 -->
      <div class="footer-menu-container volume-container">
        <div
          class="hover-button"
          @mouseenter="openPopup('volume')"
          @mouseleave="scheduleClosePopup('volume')"
        >
          <a href="javascript:;">
            <img alt="音量" class="footer-icon" :src="speakerIconSrc" @click.stop="toggleSpeakerMute" style="width: auto;height: auto;">
          </a>
        </div>
        <!-- 音量弹窗 -->
        <div
          v-if="showVolumePopup"
          class="popup-window"
          @mouseenter="openPopup('volume')"
          @mouseleave="scheduleClosePopup('volume')"
        >
          <div class="slider-container">
            <el-slider v-model="volume" vertical height="100px" :max="100" :min="0" @change="deviceStore.setSpeakerVolume" />
          </div>
          <div class="value-text">{{ volume }}%</div>
        </div>
      </div>

      <!-- 麦克风控制 -->
      <div class="footer-menu-container mic-container">
        <div
          class="hover-button"
          @mouseenter="openPopup('mic')"
          @mouseleave="scheduleClosePopup('mic')"
        >
          <a href="javascript:;">
            <img alt="麦克风" class="footer-icon" :src="micIconSrc" @click.stop="toggleMicMute">
          </a>
        </div>
        <!-- 麦克风弹窗 -->
        <div
          v-if="showMicPopup"
          class="popup-window"
          @mouseenter="openPopup('mic')"
          @mouseleave="scheduleClosePopup('mic')"
        >
          <div class="slider-container">
            <el-slider v-model="micVolume" vertical height="100px" :max="100" :min="0" @change="deviceStore.setMicVolume" />
          </div>
          <div class="value-text">{{ micVolume }}%</div>
        </div>
      </div>

      <!-- 分割线 -->
      <div v-if="deviceStore.canUseSpecialAudio" class="footer-divider"></div>

      <!-- Audio Mix -->
      <div
        ref="audioModeMenuEl"
        v-if="deviceStore.canUseSpecialAudio"
        class="footer-menu-container audio-mode-container"
        :class="{ 'is-popup-open': activeFooterPopup === 'audioMix' || audioDragging }"
        @mouseenter="openPopup('audioMix')"
        @mouseleave="scheduleClosePopup('audioMix')"
      >
        <div class="audio-balance-popup">
          <div class="audio-balance-labels">
            <span class="audio-balance-left">
              <span class="audio-label">{{ leftChannelLabel }}:</span> {{ leftDisplayPercent }}%
            </span>
            <span class="audio-balance-divider">|</span>
            <span class="audio-balance-right"><span class="audio-label">BT:</span> {{ rightDisplayPercent }}%</span>
          </div>

          <div
            ref="audioSliderEl"
            class="audio-balance-slider"
            @click.stop="onAudioSliderClick"
            @pointerdown.stop.prevent="onAudioPointerDown"
            @pointermove.stop.prevent="onAudioPointerMove"
          >
            <div class="audio-balance-track"></div>
            <div class="audio-balance-fill audio-balance-fill-right"></div>
            <div class="audio-balance-thumb"></div>
          </div>
        </div>

        <div class="audio-mode-switch">
          <a
            href="javascript:;"
            class="audio-mode-btn"
            :class="{ 'is-active': deviceStore.audioSplitEnabled }"
            @click.stop.prevent="toggleAudioMixSplit"
          >
            {{ deviceStore.audioSplitEnabled ? (t('footer.audioSplit', 'Audio Split')) : (t('footer.audioMix', 'Audio Mix')) }}
          </a>
        </div>
      </div>

      <!-- Positioning -->
      <div v-if="deviceStore.canUseSpecialAudio" class="footer-menu-container positioning-container">
        <div class="audio-mode-switch">
          <a
            href="javascript:;"
            id="xuni71"
            class="audio-mode-btn footer-menu-item"
            :class="{ 'is-active': chijiStore.enabled }"
            @click.stop.prevent="openChiji"
          >
            {{ t('main.chickenDinner', 'Positioning') }}
          </a>
        </div>
      </div>

    </div>
  </footer>
</template>

<style scoped lang="scss">
.bottom-menu {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  margin: 10px auto;
  background: rgba(19, 19, 22, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  z-index: 1100;
  width: auto;
  min-width: 480px;
  max-width: 1000px;
}

.footer-menu {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 7px;
}

.footer-menu-container {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.icon-box {
  width: 96px;
  height: 72px;
  padding: 12px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  font-size: 10px;
  border-radius: 16px;
  cursor: pointer;
  background: url('/img/footer/footer_icon.png') no-repeat;
  background-size: 100% 100%;
  margin: 0 5px;
  &:hover {
    background: url('/img/footer/footer_icon_hover.png') no-repeat;
    background-size: 100% 100%;
  }

  &.selected {
    background: url('/img/footer/on_icon_bg.png') no-repeat;
    background-size: 100% 100%;
  }

  &.selected:hover {
    background: url('/img/footer/on_icon_bg_hover.png') no-repeat;
    background-size: 100% 100%;
  }
}

.footer-icon {
  width: 28px;
  height: 28px;
  opacity: 0.8;
  transition: opacity 0.3s;
}

.icon-box.icon-box-on {
  background: url('/img/footer/on_icon_bg.png') no-repeat;
  background-size: 100% 100%;

  &:hover {
    background: url('/img/footer/on_icon_bg_hover.png') no-repeat;
    background-size: 100% 100%;
  }

  .footer-icon {
    opacity: 1;
  }
}

.footer-EQ-on {
  content: url('/img/footer/footer_EQ_on.png');
}

.footer-RGB-on {
  content: url('/img/footer/footer_RGB_on.png');
}

.footer-ENC-on {
  content: url('/img/footer/footer_ENCSwitch_on.png');
}

.footer-text {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  white-space: nowrap;

  &.noise-level {
    display: flex;
    align-items: baseline;
    gap: 2px;

    .unit {
      font-size: 10px;
    }
  }
}

.footer-text-link {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  padding: 8px 16px;
  border-radius: 4px;
  transition: background 0.3s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
}

.footer-chiji-link {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  padding: 8px 16px;
  border-radius: 4px;
  transition: background 0.3s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  .chiji-text {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.9);
    font-weight: 500;
  }

  .chiji-sub {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.5);
  }
}

.hover-button {
  cursor: pointer;
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.25);
  transition: background 0.3s;

  &:hover {
    background: rgba(255, 255, 255, 0.09);
  }
}

.footer-divider {
  width: 1px;
  height: 30px;
  background: rgba(255, 255, 255, 0.4);
  margin: -12px 20px;
}

.volume-container {
  margin: 0 25px;
}

.mic-container {
  margin: 0 10px;
}

.noise-container {
  position: relative;
}

.noise-popup {
  position: absolute;
  bottom: 52px;
  left: 50%;
  transform: translateX(-50%);
  min-width: 260px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(18px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  z-index: 50;
}

.noise-popup-title {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.92);
  margin-bottom: 10px;
}

.noise-slider-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.noise-slider-button {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
}

.noise-slider-track {
  position: relative;
  flex: 1;
  height: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  cursor: pointer;
}

.noise-slider-progress {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 0;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(0, 194, 255, 0.95), rgba(0, 102, 255, 0.95));
}

.noise-slider-thumb {
  position: absolute;
  top: 50%;
  left: 0;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
}

.noise-slider-ticks {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  display: flex;
  justify-content: space-between;
  pointer-events: none;
}

.noise-slider-ticks .tick {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.38);
  pointer-events: auto;
}

.noise-value-display {
  min-width: 22px;
  text-align: center;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
}

.pill-button {
  height: 34px;
  padding: 0 18px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(0, 0, 0, 0.15);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.25);
  }
}

.pill-text {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.75);
  white-space: nowrap;
}

/* Audio Mix / Audio Split (legacy-aligned) */
.audio-mode-container {
  position: relative;
  display: inline-flex;
  align-items: center;
  margin: 0 0 0 8px;
}

.audio-mode-switch {
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

.audio-mode-btn {
  padding: 5px 26px;
  margin: 0 10px;
  border: 0.5px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.6);
  border-radius: 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 30px;
  text-align: center;
  white-space: nowrap;
  transition: all 0.2s ease;
  user-select: none;
}

.audio-mode-btn:hover {
  border-color: rgba(255, 255, 255, 0.4);
  color: rgba(255, 255, 255, 0.8);
}

.audio-mode-btn.is-active {
  border-color: rgba(55, 107, 237, 1);
  background: rgba(55, 107, 237, 1);
  color: rgba(205, 218, 250, 1);
}

.audio-mode-btn.is-active:hover {
  border-color: rgba(95, 137, 241, 1);
  background: rgba(95, 137, 241, 1);
  color: rgba(255, 255, 255, 0.8);
}

.audio-balance-popup {
  position: absolute;
  left: -37px;
  bottom: calc(100% + 30px);
  width: 200px;
  padding: 16px 18px 18px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.16);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(18px);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.2s ease, visibility 0.2s ease;
  z-index: 20;
}

.audio-balance-popup,
.audio-balance-popup * {
  pointer-events: auto;
}

.audio-balance-popup::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -30px;
  height: 30px;
  background: transparent;
}

.audio-mode-container:hover .audio-balance-popup,
.audio-mode-container.is-popup-open .audio-balance-popup {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

.audio-balance-labels {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  width: 100%;
  margin-bottom: 16px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  line-height: 1;
}

.audio-balance-left {
  justify-self: end;
  text-align: right;
}

.audio-balance-divider {
  justify-self: center;
  margin: 0 12px;
  color: rgba(255, 255, 255, 0.2);
}

.audio-balance-right {
  justify-self: start;
  text-align: left;
}

.audio-label {
  color: rgba(255, 255, 255, 0.2);
}

.audio-balance-slider {
  position: relative;
  width: 100%;
  height: 22px;
  cursor: pointer;
  user-select: none;
  touch-action: none;

  --ab-thumb-size: 20px;
  --ab-thumb-half: calc(var(--ab-thumb-size) / 2);
  --ab-pos: 50;
  --ab-fill-start: 50;
  --ab-fill-width: 0;
}

.audio-balance-track,
.audio-balance-fill {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 8px;
  border-radius: 999px;
}

.audio-balance-track {
  left: var(--ab-thumb-half);
  width: calc(100% - var(--ab-thumb-size));
  background: rgba(24, 24, 30, 0.9);
}

.audio-balance-fill {
  left: calc(var(--ab-thumb-half) + (var(--ab-fill-start) / 100) * (100% - var(--ab-thumb-size)));
  width: calc((var(--ab-fill-width) / 100) * (100% - var(--ab-thumb-size)));
  background: rgba(55, 107, 237, 1);
}

.audio-balance-thumb {
  position: absolute;
  top: 50%;
  left: calc(var(--ab-thumb-half) + (var(--ab-pos) / 100) * (100% - var(--ab-thumb-size)));
  width: var(--ab-thumb-size);
  height: var(--ab-thumb-size);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  background: rgba(55, 107, 237, 1);
  border: 3px solid rgba(255, 255, 255, 0.96);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.28);
  cursor: grab;
  touch-action: none;
}

.audio-mode-container.is-popup-open .audio-balance-thumb {
  cursor: grabbing;
}

// 下拉菜单
.popup-dropdown {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  background: #1a1a1f;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 8px 0;
  min-width: 120px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  z-index: 1000;
}

// 弹窗
.popup-window {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  background: #1a1a1f;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  .slider-container {
    padding: 8px;
  }

  .value-text {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.6);
  }
}

.dropdown-item {
  padding: 8px 10px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.3s;
  text-align: center;
  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  &.active {
    color: #2B4EFF;
  }
}
</style>
  
